import { useState, useEffect, useCallback, RefObject } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  setStreamingConnected,
  setStreamingError,
  updateDetectionBoxes,
  setStreamingGuidanceMessage,
  setImageNextStage,
  setShowCaptureAnimation,
  resetStreamingState,
  updateStreamingMetrics,
  initStreamingSession,
} from "src/store/inspectionCaptureSlice";
import ImageStreamingWebSocketService from "src/services/imageStreamingWebSocketService";
import { getWebSocketUrl, generateClientId } from "src/config/websocketConfig";

interface UseStreamingServiceProps {
  lowResVideoRef: RefObject<HTMLVideoElement>;
  highResVideoRef: RefObject<HTMLVideoElement>;
  enabled: boolean;
  onReadyForAnalyzer?: () => void; // Callback when ready for analyzer
}

interface UseStreamingServiceReturn {
  streamingService: ImageStreamingWebSocketService | null;
  pauseStreaming: () => void;
  resumeStreaming: () => void;
}

/**
 * Custom hook to manage WebSocket streaming service lifecycle
 * Handles connection, reconnection, and cleanup for dual camera system
 */
export const useStreamingService = ({
  lowResVideoRef,
  highResVideoRef,
  enabled,
  onReadyForAnalyzer,
}: UseStreamingServiceProps): UseStreamingServiceReturn => {
  const dispatch = useAppDispatch();
  const { 
    streamingConfig, 
    streamingConnected, 
    activeQuestion, 
    activeObjectQuestions,
    screenWidth,
    screenHeight
  } = useAppSelector(
    (state) => state.inspectionCapture
  );
  const { currentCreatedInspection } = useAppSelector((state) => state.inspection);
  const [streamingService, setStreamingService] = useState<ImageStreamingWebSocketService | null>(null);

  // Handle detection results from WebSocket
  const handleDetectionResult = useCallback((result: any) => {
    if (result.status === "success") {
      // Update detection boxes
      if (result.bboxes) {
        dispatch(updateDetectionBoxes(result.bboxes));
      }
      
      // Update guidance message
      if (result.guidance_message) {
        dispatch(setStreamingGuidanceMessage(result.guidance_message));
      }
      
      // Handle image_next_stage transitions
      if (result.image_next_stage) {
        dispatch(setImageNextStage(result.image_next_stage));
        
        // Switch WebSocket stream based on backend signal
        if (result.image_next_stage === 'HIGH_RES') {
          streamingService?.switchToHighRes();
        } else if (result.image_next_stage === 'LOW_RES') {
          streamingService?.switchToLowRes();
        } else if (result.image_next_stage === 'READY_FOR_ANALYZER') {
          // Stop streaming when ready for analyzer
          streamingService?.pauseStreaming();
          dispatch(setShowCaptureAnimation(true));
          
          // Hide animation and trigger modal after 1 second
          setTimeout(() => {
            dispatch(setShowCaptureAnimation(false));
            // Trigger modal to ask user to move to next
            if (onReadyForAnalyzer) {
              onReadyForAnalyzer();
            }
          }, 1000);
        }
      }
    }
  }, [dispatch, streamingService]);

  // Handle WebSocket connection status
  const handleConnectionStatus = useCallback((connected: boolean) => {
    dispatch(setStreamingConnected(connected));
    if (!connected) {
      dispatch(setStreamingError("Connection lost"));
    } else {
      dispatch(setStreamingError(null));
    }
  }, [dispatch]);

  // Handle metrics updates from WebSocket
  const handleMetricsUpdate = useCallback((metrics: { imageSize?: number; latency?: number }) => {
    dispatch(updateStreamingMetrics(metrics));
  }, [dispatch]);

  // Initialize WebSocket streaming service
  useEffect(() => {
    if (enabled && !streamingService && lowResVideoRef.current && highResVideoRef.current) {
      const wsUrl = getWebSocketUrl();
      const clientId = generateClientId();
      const inspectionId = currentCreatedInspection.inspectionId;

      // Only create service if we have a valid inspection ID
      if (!inspectionId) {
        console.warn("Cannot initialize streaming service: No inspection ID available");
        dispatch(setStreamingError("No inspection ID available for streaming"));
        return;
      }

      // Extract active question IDs based on inspection mode
      const activeQuestionIds = activeObjectQuestions.length > 0
        ? activeObjectQuestions.map(q => q.questionId).filter((id): id is string => id !== null && id !== undefined)
        : activeQuestion?.questionId ? [activeQuestion.questionId] : [];

      const service = new ImageStreamingWebSocketService(
        wsUrl, 
        clientId, 
        inspectionId,
        activeQuestionIds,
        {
          quality: streamingConfig.quality,
          maxWidth: streamingConfig.maxWidth,
          maxHeight: streamingConfig.maxHeight,
          interval: streamingConfig.interval,
          screenWidth: screenWidth,
          screenHeight: screenHeight,
          resolutionScale: streamingConfig.resolutionScale
        }
      );

      setStreamingService(service);
    }

    return () => {
      if (streamingService && !enabled) {
        streamingService.stopStreaming();
        dispatch(resetStreamingState());
        setStreamingService(null);
      }
    };
  }, [enabled, lowResVideoRef.current, highResVideoRef.current, streamingConfig, currentCreatedInspection.inspectionId]);

  // Connection health monitoring
  useEffect(() => {
    if (enabled && !streamingConnected && streamingService) {
      if (streamingService.getConnectionStatus() === 'closed') {
        streamingService.resumeStreaming();

        setTimeout(() => {
          if (!streamingConnected) {
            dispatch(setStreamingError("Reconnection failed"));
          }
        }, 2000);
      }
    }
  }, [enabled, streamingConnected, streamingService]);

  // Start streaming when service is available
  useEffect(() => {
    const startStreaming = async () => {
      if (streamingService && lowResVideoRef.current && highResVideoRef.current && enabled) {
        try {
          await streamingService.startStreaming(
            lowResVideoRef.current,
            highResVideoRef.current,
            handleDetectionResult,
            handleConnectionStatus,
            handleMetricsUpdate
          );

          dispatch(initStreamingSession());
        } catch (error) {
          dispatch(setStreamingError("Failed to connect to streaming service"));
        }
      }
    };

    startStreaming();
  }, [streamingService, lowResVideoRef.current, highResVideoRef.current, enabled]);

  // Pause streaming
  const pauseStreaming = useCallback(() => {
    if (streamingService) {
      streamingService.pauseStreaming();
    }
  }, [streamingService]);

  // Resume streaming
  const resumeStreaming = useCallback(() => {
    if (streamingService && streamingService.isConnected()) {
      streamingService.resumeStreaming();
    }
  }, [streamingService]);

  return {
    streamingService,
    pauseStreaming,
    resumeStreaming,
  };
};
